import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import SiteMobileHeader from '../components/SiteMobileHeader';
import NewsArticleCard from '../components/NewsArticleCard';
import homeHeroLogo from '../assets/Group12.svg';
import MailingListModal from '../components/MailingListModal';
import StickyNavigation from '../components/StickyNavigation';
import HomeContactFooter from '../components/HomeContactFooter';
import { mediaHeroImageDesktop } from '../cdn/mediaCentreMedia';
import iconsaxYoutube from '../assets/iconsax-youtube.svg';
import { clearContactState, submitMailingListForm } from '../store/slices/contactSlice';
import { validateMailingListForm } from '../utils/mailingListValidation';
import { showToast } from '../utils/toast';
import { useStaggeredRiseOnView, staggerLineClass } from '../hooks/useStaggeredRiseOnView';
import '../pages/LandingPage.css';
import './MediaCentrePage.css';
import '../styles/SitePageShell.css';

const CONTENT_RAIL = 'mc-content-rail';

const EVENTS_VIDEO_DESCRIPTION =
  'Neoterra marks the beginning of an exciting new chapter in Dubai real estate, bringing together thoughtful design, modern living, and a clear vision for the future. The launch introduces a brand built around creating refined spaces, meaningful experiences, and lasting value for homeowners and investors.';

const getYouTubeThumb = (youtubeId) => `https://i.ytimg.com/vi/${youtubeId}/hq720.jpg`;

const EVENTS_VIDEO_ITEMS = [
  {
    id: 'eira-model-launch',
    youtubeId: '-VU-zFixosk',
    title: 'EIRA Model Launch | A First Look at Neoterra’s Vision for Modern Living',
    description: EVENTS_VIDEO_DESCRIPTION,
  },
  {
    id: 'eira-allocation-day',
    youtubeId: 'HFLddW3DJXY',
    title: 'EIRA Allocation Day | Celebrating a New Milestone in the EIRA Journey',
    description: EVENTS_VIDEO_DESCRIPTION,
  },
  {
    id: 'elmora-launch-event',
    youtubeId: '5N31mM7GlVY',
    title: 'Elmora Launch Event | Introducing a New Chapter of Luxury Living in Dubai',
    description: EVENTS_VIDEO_DESCRIPTION,
  },
  {
    id: 'eira-by-neoterra',
    youtubeId: 'AMgRbsopYHU',
    title: 'EIRA by Neoterra | Discover Thoughtfully Designed Homes in Dubai Production City',
    description: EVENTS_VIDEO_DESCRIPTION,
  },
  {
    id: 'elmora-furnished-homes',
    youtubeId: 'akvIXj-oe-k',
    title: 'Elmora | Fully furnished luxury homes from £224k, off Sheikh Zayed Road, Dubai',
    description: EVENTS_VIDEO_DESCRIPTION,
  },
  {
    id: 'eira-broker-breakfast',
    youtubeId: 'JReh_pArN5Y',
    title: 'EIRA Broker Breakfast Briefing | Connecting Partners & Sharing a New Vision for Modern Living',
    description: EVENTS_VIDEO_DESCRIPTION,
  },
].map((item) => ({
  ...item,
  image: getYouTubeThumb(item.youtubeId),
}));

const EVENTS_VIDEOS_PAGE_SIZE = 3;

const truncateEventsSummaryForMobile = (text, viewportW) => {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length <= 4) {
    return words.join(' ');
  }

  const cardWidth = viewportW * 0.84 * 0.898;
  const charsPerLine = Math.max(22, Math.floor(cardWidth / 6.4));
  const twoLineBudget = charsPerLine * 2;

  let used = 0;
  let index = 0;
  while (index < words.length) {
    const nextLen = used === 0 ? words[index].length : used + 1 + words[index].length;
    if (nextLen > twoLineBudget) {
      break;
    }
    used = nextLen;
    index += 1;
  }

  if (index < 2) {
    index = Math.min(2, words.length);
  }

  const shown = words.slice(0, index + 2);
  return shown.length < words.length ? `${shown.join(' ')}...` : shown.join(' ');
};

const MAIL_MODAL_CLOSE_MS = 620;
const VIDEO_MODAL_CLOSE_MS = 620;

const getYouTubeWatchUrl = (youtubeId) => `https://www.youtube.com/watch?v=${youtubeId}`;

const MC_REVEAL_LINE = 'mc-reveal-line';

const MediaCentrePage = () => {
  const dispatch = useDispatch();
  const contactSubmitting = useSelector((state) => state.contact.loading);
  const [videoPlayingIndex, setVideoPlayingIndex] = useState(null);
  const [videoPlayingSource, setVideoPlayingSource] = useState(null);
  const [videoClosing, setVideoClosing] = useState(false);
  const [eventsExpanded, setEventsExpanded] = useState(false);
  const videoPlayingRef = useRef(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailClosing, setMailClosing] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [promotionalConsent, setPromotionalConsent] = useState(false);
  const [heroIntroComplete, setHeroIntroComplete] = useState(false);
  const heroStageRef = useRef(null);
  const heroVideoRef = useRef(null);
  const mcEventsRevealRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 402));

  useStaggeredRiseOnView(mcEventsRevealRef, {
    individual: true,
    observeMutations: true,
    durationMs: 2200,
    distancePx: 48,
    threshold: 0.14,
    rootMargin: '0px 0px 10% 0px',
    replay: true,
    reverseOnLeave: true,
    desktopOnly: false,
  });

  const scrollToContent = () => {
    const target = document.getElementById('media-centre');
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - 46;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  useEffect(() => {
    videoPlayingRef.current = videoPlayingIndex;
  }, [videoPlayingIndex]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth || 402);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setHeroIntroComplete(true);
      return undefined;
    }

    const stage = heroStageRef.current;
    if (!stage) return undefined;

    const shell = stage.querySelector('.mc-hero-desktop__image-shell');
    if (!shell) return undefined;

    const completeIntro = () => setHeroIntroComplete(true);

    const onAnimationEnd = (event) => {
      if (event.target !== shell || event.animationName !== 'mcHeroImageScale') return;
      completeIntro();
    };

    const fallbackTimer = window.setTimeout(completeIntro, 2700);

    shell.addEventListener('animationend', onAnimationEnd);

    return () => {
      shell.removeEventListener('animationend', onAnimationEnd);
      window.clearTimeout(fallbackTimer);
    };
  }, [viewportWidth]);

  useEffect(() => {
    if (!heroIntroComplete) return undefined;

    const el = heroVideoRef.current;
    if (!el) return undefined;

    const tryPlay = () => {
      const playPromise = el.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => { });
      }
    };

    tryPlay();
    el.addEventListener('canplay', tryPlay);

    return () => el.removeEventListener('canplay', tryPlay);
  }, [heroIntroComplete]);

  useEffect(() => {
    if (viewportWidth < 1024) return undefined;
    if (videoPlayingIndex == null && !videoClosing) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [videoPlayingIndex, videoClosing, viewportWidth]);

  const openMailModal = () => {
    setMailClosing(false);
    setMailOpen(true);
  };

  const closeMailModal = () => {
    if (mailClosing || !mailOpen) return;
    setMailClosing(true);
    window.setTimeout(() => {
      setMailOpen(false);
      setMailClosing(false);
    }, MAIL_MODAL_CLOSE_MS);
  };

  const handleMailSubmit = async (e) => {
    e.preventDefault();
    const validation = validateMailingListForm({ fullName, email, phone });

    if (!validation.isValid) {
      showToast(validation.error, 'error');
      return;
    }

    const { name, email: emailValue, contactNumber } = validation.values;

    try {
      await dispatch(
        submitMailingListForm({
          name,
          email: emailValue,
          contactNumber,
          promotionalConsent,
          sourcePage: 'media',
        })
      ).unwrap();
      showToast('Thanks! We will keep you informed.', 'success');
      setFullName('');
      setEmail('');
      setPhone('');
      setPromotionalConsent(false);
      closeMailModal();
    } catch (err) {
      showToast(typeof err === 'string' ? err : 'Something went wrong. Please try again.', 'error');
    } finally {
      dispatch(clearContactState());
    }
  };

  const closeVideoModal = useCallback(() => {
    if (videoClosing || videoPlayingIndex == null) return;
    setVideoClosing(true);
    window.setTimeout(() => {
      setVideoPlayingIndex(null);
      setVideoPlayingSource(null);
      setVideoClosing(false);
    }, VIDEO_MODAL_CLOSE_MS);
  }, [videoClosing, videoPlayingIndex]);

  const openEventsVideoModal = (index) => {
    setVideoClosing(false);
    setVideoPlayingSource('events');
    setVideoPlayingIndex(index);
  };

  const handleLoadMoreEvents = () => {
    setEventsExpanded(true);
  };

  useEffect(() => {
    if (videoPlayingIndex == null) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeVideoModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [videoPlayingIndex, closeVideoModal]);

  const eventsVisibleCount = eventsExpanded
    ? EVENTS_VIDEO_ITEMS.length
    : Math.min(EVENTS_VIDEOS_PAGE_SIZE, EVENTS_VIDEO_ITEMS.length);
  const hasMoreEvents = eventsVisibleCount < EVENTS_VIDEO_ITEMS.length;
  const videoItems = EVENTS_VIDEO_ITEMS;
  const activeVideo = videoPlayingIndex != null ? videoItems[videoPlayingIndex] : null;
  const useInlineVideoPlayer = viewportWidth < 1024;
  const videoModal =
    !useInlineVideoPlayer && activeVideo?.youtubeId && videoPlayingIndex != null
      ? createPortal(
        <div
          className={`mc-video-modal-backdrop${videoClosing ? ' is-closing' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label={`Video: ${activeVideo.title}`}
          onClick={closeVideoModal}
        >
          <div
            className={`mc-video-modal${videoClosing ? ' is-closing' : ''}`}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="mc-video-modal__close"
              aria-label="Close video"
              onClick={closeVideoModal}
            >
              ×
            </button>
            <div className="mc-video-modal__frame">
              <iframe
                className="h-full w-full border-0"
                src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1&playsinline=1&rel=0&modestbranding=1`}
                title={activeVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </div>,
        document.body
      )
      : null;

  return (
    <div className={`media-centre-page site-page-shell relative min-h-screen overflow-x-hidden bg-white lg:bg-[#FFF7EA] font-['Poppins',sans-serif] text-[#0a0a0a]${heroIntroComplete ? ' mc-hero-intro-complete' : ''}`}>
      <SiteMobileHeader
        variant="homeHero"
        logoSrc={homeHeroLogo}
        barClassName="home-top-bar media-centre-home-top-bar"
        onEnquireClick={openMailModal}
      />

      <div className="relative flex min-h-screen flex-col">
        <section className="relative h-[100dvh] min-h-[100dvh] w-full overflow-hidden" aria-label="Media centre">
          <div className="mc-hero-desktop relative z-[2] block h-full w-full overflow-hidden">
            <div ref={heroStageRef} className="mc-hero-desktop__stage relative h-full w-full bg-[#FFF7EA]">
              <div className="mc-hero-desktop__image-shell">
                <figure className="mc-hero-desktop__figure m-0 h-full w-full overflow-hidden">
                  <img
                    src={mediaHeroImageDesktop}
                    alt=""
                    className="mc-hero-desktop__image block h-full w-full object-cover"
                    decoding="async"
                    fetchPriority="high"
                    width={931}
                    height={524}
                  />
                </figure>
                <video
                  ref={heroVideoRef}
                  className="mc-hero-desktop__video absolute inset-0 z-[1] h-full w-full object-cover object-center"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                >
                  <source src="https://neoterra-site.b-cdn.net/Eira_walkthrough.mp4" type="video/mp4" />
                </video>
                <div className="mc-hero-desktop__video-overlay pointer-events-none absolute inset-0 z-[2]" aria-hidden="true" />
              </div>
              <div
                className={`${CONTENT_RAIL} mc-hero-desktop__copy pointer-events-none absolute inset-x-0 top-0 z-[3] mx-auto h-full`}
                aria-hidden={heroIntroComplete}
              >
                <h1 className="mc-hero-headline mc-hero-headline--intro m-0">
                  <span className="mc-hero-headline__group">
                    <span className="mc-hero-headline__word mc-hero-headline__word--media">
                      Media
                    </span>
                    <span className="mc-hero-headline__word mc-hero-headline__word--gallery">
                      Gallery
                    </span>
                  </span>
                </h1>
              </div>
            </div>
          </div>
        </section>

        <StickyNavigation wrapperClassName="home-navigation-sticky lg:hidden" onInquireClick={openMailModal} />

        <main id="media-centre" className="mx-auto w-full flex-[1_0_auto] bg-white pb-0 lg:bg-[#FFF7EA]" aria-label="Media centre content">
          <div className="contents">
            <section
              ref={mcEventsRevealRef}
              className="mc-events-videos bg-white pt-16 pb-10 lg:py-[calc(80px*var(--mc-from-1920,0.75))]"
              aria-label="Events videos"
            >
              <div className={CONTENT_RAIL}>
                <h2
                  data-content-line
                  className={`mc-events-videos__heading m-0 ${MC_REVEAL_LINE}`}
                >
                  <p
                    className={`press-release-publishers-desktop__kicker ${staggerLineClass}`}
                    data-content-line
                  >
                    <span className="press-release-publishers-desktop__kicker-line" aria-hidden="true" />
                    EVENTS
                  </p><br />
                  Events Videos
                </h2>

                <div className="press-release-publishers-desktop__grid mt-[calc(48px*var(--mc-from-1920,0.75))]">
                  {EVENTS_VIDEO_ITEMS.slice(0, eventsVisibleCount).map((item, index) => {
                    const isLoadMoreCard = index >= EVENTS_VIDEOS_PAGE_SIZE;
                    const loadBatchIndex = isLoadMoreCard ? index - EVENTS_VIDEOS_PAGE_SIZE : 0;
                    const loadCardOffset = loadBatchIndex * 180;
                    const revealDelay = (partOffsetMs) =>
                      isLoadMoreCard ? String(loadCardOffset + partOffsetMs) : undefined;

                    return (
                      <div
                        key={`events-video-${item.id}`}
                        className={`press-release-publishers-desktop__grid-item ${MC_REVEAL_LINE}`}
                        data-content-line=""
                        data-reveal-delay={revealDelay(0)}
                      >
                        <NewsArticleCard
                          layout="videoDesktop"
                          className="press-release-publishers-desktop__card"
                          hidePublisherLogo
                          imageSrc={item.image}
                          imageAlt={item.title}
                          imageFit="cover"
                          title={item.title}
                          summary={
                            viewportWidth < 1024
                              ? truncateEventsSummaryForMobile(item.description, viewportWidth)
                              : item.description
                          }
                          onPlayClick={() => openEventsVideoModal(index)}
                          isVideoPlaying={useInlineVideoPlayer && videoPlayingIndex === index}
                          youtubeId={item.youtubeId}
                          watchNowIconSrc={iconsaxYoutube}
                          readMoreHref={getYouTubeWatchUrl(item.youtubeId)}
                          readMoreAriaLabel={`Watch video: ${item.title}`}
                          readMoreNewTab
                        />
                      </div>
                    );
                  })}
                </div>

                {hasMoreEvents ? (
                  <div className="mt-[calc(56px*var(--mc-from-1920,0.75))] flex justify-center">
                    <button
                      type="button"
                      className="mc-events-videos__load-more"
                      aria-label="Load more events videos"
                      onClick={handleLoadMoreEvents}
                    >
                      Load More
                      <i aria-hidden="true" />
                    </button>
                  </div>
                ) : null}
              </div>
            </section>
          </div>
        </main>
      </div>

      <div className="relative z-[20] lg:hidden">
        <HomeContactFooter sectionId="media-centre-contact" />
      </div>

      <div className="site-page-footer-wrap relative z-[30] isolate hidden bg-[#162D24] lg:block">
        <HomeContactFooter
          variant="home"
          sectionId="media-centre-contact"
        />
      </div>

      <MailingListModal
        open={mailOpen}
        closing={mailClosing}
        onClose={closeMailModal}
        onSubmit={handleMailSubmit}
        submitting={contactSubmitting}
        fullName={fullName}
        setFullName={setFullName}
        email={email}
        setEmail={setEmail}
        phone={phone}
        setPhone={setPhone}
        promotionalConsent={promotionalConsent}
        setPromotionalConsent={setPromotionalConsent}
        idPrefix="media-mail"
      />

      {videoModal}
    </div>
  );
};

export default MediaCentrePage;
